import { useEffect, useState } from "react";
import axiosInstant from "../../api";
import { proposalSuggestEndpoint } from "../../api/endpoints";

export interface ProposalSuggestion {
    pdp_audit_report_id: number | null;
    pdp_url_score_id?: number | null;
    consumer_voice_audit_id?: number | null;
    ai_presence_audit_id?: number | null;
    product_category_id: number | null;
    objective_id: number | null;
    secondary_objective_id: number | null;
    rationale: string | null;
    // The strategy's description, drawn from the analysis; sent on create.
    description?: string | null;
}

// Gemini prefill for the wizard's category + primary/secondary objectives,
// drawn from whichever audit the client came in from:
// - pdpAuditReportId: a PDP audit report (single audit, or a brand /
//   category / comparison report — pass `url` so the server picks the page
//   the client clicked out of that report's items);
// - pdpUrlScoreId: a catalog product's latest PDP score (Products page).
// One-shot, non-fatal — no suggestion just means the wizard starts blank.
// Anonymous callers must supply the audit's own email (ownership check).
// - consumerVoiceAuditId: a Consumer Voice analysis (what shoppers say on
//   Reddit); anonymous callers pass the row's access token instead of email.
// - aiPresenceAuditId: an AI Presence analysis (whether AI shopping answers
//   mention and recommend the product); same access-token rule.
export interface SuggestionSource {
    pdpAuditReportId?: number | null;
    pdpUrlScoreId?: number | null;
    consumerVoiceAuditId?: number | null;
    aiPresenceAuditId?: number | null;
    // The Consumer Voice / AI Presence row's access token (anonymous runs).
    accessToken?: string | null;
    url?: string | null;
}

export const useProposalSuggestions = (
    source: SuggestionSource,
    email: string,
    enabled: boolean,
) => {
    const pdpAuditReportId = source.pdpAuditReportId || null;
    const pdpUrlScoreId = source.pdpUrlScoreId || null;
    const consumerVoiceAuditId = source.consumerVoiceAuditId || null;
    const aiPresenceAuditId = source.aiPresenceAuditId || null;
    const accessToken = source.accessToken || null;
    const url = source.url || null;
    const [suggestion, setSuggestion] = useState<ProposalSuggestion | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled || (!pdpAuditReportId && !pdpUrlScoreId && !consumerVoiceAuditId && !aiPresenceAuditId)) return;
        let cancelled = false;
        setLoading(true);
        let sourceBody: Record<string, unknown> = { pdp_audit_report_id: pdpAuditReportId };
        if (consumerVoiceAuditId) {
            sourceBody = {
                consumer_voice_audit_id: consumerVoiceAuditId,
                ...(accessToken ? { access_token: accessToken } : {}),
            };
        } else if (aiPresenceAuditId) {
            sourceBody = {
                ai_presence_audit_id: aiPresenceAuditId,
                ...(accessToken ? { access_token: accessToken } : {}),
            };
        } else if (pdpUrlScoreId) {
            sourceBody = { pdp_url_score_id: pdpUrlScoreId };
        }
        (async () => {
            try {
                const { data } = await axiosInstant.post<ProposalSuggestion>(
                    proposalSuggestEndpoint,
                    {
                        ...sourceBody,
                        ...(url ? { url } : {}),
                        email: email || undefined,
                    },
                );
                if (!cancelled) setSuggestion(data);
            } catch {
                if (!cancelled) setSuggestion(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdpAuditReportId, pdpUrlScoreId, consumerVoiceAuditId, aiPresenceAuditId, url, enabled]);

    return { suggestion, loading };
};
